import { shortMonthAndYear } from "@similarweb/pro-logic-components";

import type {
    IData,
    IDataRecord,
    ISitesData,
} from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import DurationService from "app/services/DurationService";

import { getShareValue, getVolumeValue } from "../../../../website-analysis/traffic-sources/search/common/utils";

import { calcDomainsPositions } from "./Utilities";

export const transformData = (duration: string, displayNumberOfRows?: number) => (data: IData) => {
    const updatedData = displayNumberOfRows ? data.Data.Records.slice(0, displayNumberOfRows) : data.Data.Records;
    const transformedData = updatedData.map((dataUnit: IDataRecord) => {
        const getExtractedDataFromSitesData = (): [
            Record<string, string[]>,
            Record<string, number>,
            Record<string, number | null>,
            Record<string, number | null>,
            Record<string, number | null>,
        ] => {
            if (!Array.isArray(dataUnit.SitesData)) {
                return [{}, {}, {}, {}, {}];
            }
            const siteSerpFeatures = {};
            const siteOrigins = {};
            const SiteClicks = {};
            const PreviousPosition: Record<string, number | null> | number = {};
            const Position: Record<string, number | null> | number = {};
            dataUnit.SitesData.forEach((site: ISitesData) => {
                siteSerpFeatures[site.Site] = site.SerpFeatures;
                siteOrigins[site.Site] = site.Share;
                SiteClicks[site.Site] = site.Value;
                PreviousPosition[site.Site] = site.PreviousPosition ?? null;
                Position[site.Site] = site.Position ?? null;
            });
            return [siteSerpFeatures, siteOrigins, PreviousPosition, Position, SiteClicks];
        };
        const extractedSitesData = getExtractedDataFromSitesData();
        const shareOfDesktop = dataUnit.DesktopClicks / dataUnit.Clicks;
        const organicClicksShare = dataUnit.OrganicClicks / dataUnit.Clicks;
        const durationText = DurationService.getFormattedDateOutOfDurationParam(duration, shortMonthAndYear);
        return {
            ...dataUnit,
            Share: getShareValue(dataUnit.Share),
            Clicks: getVolumeValue(dataUnit.Clicks),
            KwWindowVolume: getVolumeValue(dataUnit.KwWindowVolume),
            KwVolume: getVolumeValue(dataUnit.KwVolume),
            KwVolumeAverage: getVolumeValue(dataUnit.KwVolumeAverage),
            ClicksChange: {
                first: dataUnit.PreviousClicks,
                last: dataUnit.Clicks,
                change: dataUnit.Change,
                changeState: dataUnit.ChangeState,
            },
            VolumeChange: {
                first: dataUnit?.KwWindowVolume,
                last: dataUnit?.PreviousVolume,
                change: dataUnit?.VolumeChange,
                changeState: dataUnit?.VolumeChangeState,
            },
            PreviousClicksPop: getVolumeValue(dataUnit.PreviousClicksPop),
            SearchTerm: dataUnit.Keyword,
            WebSourceShare: shareOfDesktop,
            OrganicClicksShare: organicClicksShare,
            SerpScrapeDate: dataUnit.LatestKeywordSerpDate,
            SiteSerpFeatures: extractedSitesData[0],
            SiteOrigins: extractedSitesData[1],
            SiteClicks: extractedSitesData[4],
            PositionLeader: calcDomainsPositions(dataUnit.SitesData),
            PreviousPosition: extractedSitesData[2],
            CurrentPosition: extractedSitesData[3],
            UrlCount: dataUnit.UrlCount,
            Position: dataUnit.SitesData.map((site: ISitesData) => {
                return {
                    Key: site.Site,
                    Value: site.Position ?? -1,
                };
            }),

            TopURLs: dataUnit.SitesData.map((site: ISitesData) => {
                return {
                    Key: site.Site,
                    Value: site.TopUrl ?? "",
                };
            }),
            Intent: {
                PrimaryIntent: dataUnit.PrimaryIntent,
                SecondaryIntent: dataUnit.SecondaryIntent,
            },
            durationText,
            getDifficultyBarTexts: (translate, difficultyLevel, country) => {
                return {
                    labelText: translate(`keyword.difficulty.level.${difficultyLevel}.label`),
                    easyLevelText: translate("keyword.difficulty.level.easy.label"),
                    difficultLabelText: translate("keyword.difficulty.level.difficult.label"),
                    disclaimerText: translate(`keyword.competition.level.${difficultyLevel}.disclaimer`, {
                        country,
                    }),
                };
            },
        };
    });
    return {
        ...data,
        Data: transformedData,
    };
};
